import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Aggressively forces scroll to top on route changes
 * This component continuously enforces scroll position until confirmed at top
 */
const ForceScrollToTop = () => {
  const location = useLocation();
  const prevPathRef = useRef(location.pathname);

  // Scroll all possible scrollable containers to top
  const scrollAllToTop = () => {
    // Disable smooth scrolling temporarily
    const html = document.documentElement;
    const body = document.body;
    const originalHtmlScrollBehavior = html.style.scrollBehavior;
    const originalBodyScrollBehavior = body.style.scrollBehavior;
    
    html.style.scrollBehavior = 'auto';
    body.style.scrollBehavior = 'auto';
    
    // Window scroll - multiple methods
    window.scrollTo(0, 0);
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } catch (e) {
      // Fallback
      window.scrollTo(0, 0);
    }
    
    // Document element scroll
    if (document.documentElement) {
      document.documentElement.scrollTop = 0;
      document.documentElement.scrollLeft = 0;
    }
    
    // Body scroll
    if (document.body) {
      document.body.scrollTop = 0;
      document.body.scrollLeft = 0;
    }
    
    // Root element scroll
    const root = document.getElementById('root');
    if (root) {
      root.scrollTop = 0;
      root.scrollLeft = 0;
    }
    
    // Try scrollIntoView
    try {
      if (document.documentElement) {
        document.documentElement.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
      if (document.body) {
        document.body.scrollIntoView({ behavior: 'instant', block: 'start' });
      }
    } catch (e) {
      // Ignore
    }
    
    // Restore scroll behavior after a short delay
    setTimeout(() => {
      html.style.scrollBehavior = originalHtmlScrollBehavior || '';
      body.style.scrollBehavior = originalBodyScrollBehavior || '';
    }, 50);
  };

  // Immediate scroll on pathname change - BEFORE render
  useLayoutEffect(() => {
    if (location.pathname !== prevPathRef.current || 
        location.search !== prevPathRef.current.split('?')[1] ||
        location.hash !== prevPathRef.current.split('#')[1]) {
      prevPathRef.current = location.pathname + location.search + location.hash;
      
      // Force scroll immediately, multiple times to ensure it sticks
      scrollAllToTop();
      scrollAllToTop();
      scrollAllToTop();
    }
  }, [location.pathname, location.search, location.hash]);

  // Continuous enforcement after render
  useEffect(() => {
    const pathKey = location.pathname + location.search + location.hash;
    if (pathKey !== prevPathRef.current) {
      prevPathRef.current = pathKey;
      
      const enforceScroll = () => {
        const currentScroll = window.scrollY || 
                            document.documentElement.scrollTop || 
                            document.body.scrollTop ||
                            (document.getElementById('root')?.scrollTop || 0);
        
        if (currentScroll > 0) {
          scrollAllToTop();
        }
      };

      // Immediate
      enforceScroll();

      // Multiple rapid attempts
      const timeouts = [
        setTimeout(enforceScroll, 0),
        setTimeout(enforceScroll, 1),
        setTimeout(enforceScroll, 5),
        setTimeout(enforceScroll, 10),
        setTimeout(enforceScroll, 25),
        setTimeout(enforceScroll, 50),
        setTimeout(enforceScroll, 100),
        setTimeout(enforceScroll, 200),
        setTimeout(enforceScroll, 300),
        setTimeout(enforceScroll, 500),
      ];

      // Use requestAnimationFrame
      let rafCount = 0;
      const rafLoop = () => {
        if (rafCount < 20) { // Run for ~20 frames
          enforceScroll();
          rafCount++;
          requestAnimationFrame(rafLoop);
        }
      };
      requestAnimationFrame(rafLoop);

      // Also use intervals
      const intervals = [
        setInterval(enforceScroll, 10),   // Every 10ms
        setInterval(enforceScroll, 50),   // Every 50ms
      ];

      // Stop after 2 seconds
      const stopTimeout = setTimeout(() => {
        intervals.forEach(clearInterval);
        timeouts.forEach(clearTimeout);
      }, 2000);

      return () => {
        intervals.forEach(clearInterval);
        timeouts.forEach(clearTimeout);
        clearTimeout(stopTimeout);
      };
    }
  }, [location.pathname, location.search, location.hash]);

  return null;
};

export default ForceScrollToTop;
